import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import styled from 'styled-components/native';
import AddressAutocompleteInput, {
  toFormAddress
} from '../../../common/AddressAutocompleteInput';
import { isAddressComplete } from '../../../common/isAddressComplete';
import { UserAddress } from '../../../models/User';
import { useDynamicFormContext } from '../../DynamicFormContext';
import { FormElementProps } from '../../FormElement';
import { FormElementPlugin } from '../FormElementPlugin';
import FormElementQuestion from './components/FormElementQuestion';

const FormInputView = styled.View`
  margin-top: 8px;
  width: 100%;
`;

export interface FormAddressConfig {
  placeholder?: string;
}

/**
 * Address form element — single-line Places autocomplete bound to the reserved
 * user field `address`. Server-side assertStorable still gates geofence-quality
 * addresses when the form upserts the user.
 */
export const FormAddress = <FormValuesType extends object>({
  element,
  disabled
}: FormElementProps<FormValuesType, FormAddressConfig>) => {
  const key = 'address' as keyof FormValuesType;
  if (!element?.dataSource) element.dataSource = 'address';

  const { formValues, setFormValue } = useDynamicFormContext<
    FormValuesType,
    FormAddressConfig
  >();
  const [value, setValue] = useState<UserAddress | null>(
    (formValues[key] as unknown as UserAddress) || null
  );
  const previousValueRef = useRef(value);

  useEffect(() => {
    const nextFormValue = formValues[key] as unknown as UserAddress | null;
    if (previousValueRef.current !== nextFormValue) {
      setValue(nextFormValue ?? null);
      previousValueRef.current = nextFormValue ?? null;
    }
  }, [formValues, key]);

  const handleChange = useCallback(
    (address: UserAddress | null) => {
      const next = toFormAddress(address);
      setValue(next);
      previousValueRef.current = next;
      setFormValue(key, next as any, element);
    },
    [setFormValue, element, key]
  );

  const placeholder = element.config?.placeholder || '';

  return (
    <View>
      <FormElementQuestion element={element} />
      <FormInputView>
        <AddressAutocompleteInput
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
        />
      </FormInputView>
    </View>
  );
};

export const FormAddressPlugin: FormElementPlugin<any, FormAddressConfig> = {
  name: 'Address',
  type: 'Address',
  element: FormAddress as React.FC<FormElementProps<any, FormAddressConfig>>,
  validate: (element, formValues) =>
    !element.required || isAddressComplete(formValues?.address)
};
